<template>
    <section class="news-detail-page h-full w-full flex flex-col">
        <!-- Hero Section -->
        <div :style="backgroundStyle" class="news-hero relative w-full overflow-hidden">
            <div class="hero-overlay"></div>
            <div class="hero-content">
                <div class="container mx-auto px-4 sm:px-6 lg:px-8 h-full flex items-center">
                    <div class="max-w-3xl">
                        <div class="hero-badge">
                            <span>News Article</span>
                        </div>
                        <h1 v-if="news" class="hero-title">{{ news.title }}</h1>
                        <h1 v-else class="hero-title">Loading...</h1>
                    </div>
                </div>
            </div>
        </div>

        <!-- Main Content -->
        <div class="news-content bg-gradient-to-b from-gray-50 to-white pt-4 pb-12 sm:pt-6 sm:pb-16">
            <div class="container mx-auto px-4 sm:px-6 lg:px-8">
                <!-- Breadcrumb -->
                <div class="mb-4">
                    <bread-crumb :items="breadCrumbItems"></bread-crumb>
                </div>

                <!-- Loading State -->
                <div v-if="loading" class="loading-container">
                    <div class="loading-header">
                        <div class="loading-bar"></div>
                    </div>
                    <div class="loading-content">
                        <div class="loading-image"></div>
                        <div class="loading-title"></div>
                        <div class="loading-meta"></div>
                        <div class="loading-description"></div>
                        <div class="loading-text-lines">
                            <div v-for="(i, index) in 8" :key="index" class="loading-line"></div>
                        </div>
                    </div>
                </div>

                <!-- Error State -->
                <div v-else-if="error" class="error-container">
                    <div class="error-header">
                        <button @click.stop="$router.go(-1)" class="back-button-icon">
                            <icon-back class="icon"></icon-back>
                        </button>
                        <h2 class="error-title">News Not Found</h2>
                    </div>
                    <div class="error-content">
                        <div class="error-icon">
                            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                    d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                            </svg>
                        </div>
                        <p class="error-message">{{ error }}</p>
                        <button @click.stop="$router.go(-1)" class="error-back-btn">
                            <span>Go Back</span>
                            <svg class="error-back-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                    d="M10 19l-7-7m0 0l7-7m-7 7h18">
                                </path>
                            </svg>
                        </button>
                    </div>
                </div>

                <!-- News Content -->
                <div v-else-if="news" class="news-article">
                    <!-- Section Header -->
                    <div class="section-header mb-6">
                        <div class="flex items-center gap-4">
                            <button @click.stop="$router.go(-1)" class="back-button-icon">
                                <icon-back class="icon"></icon-back>
                            </button>
                            <div>
                                <h2 class="section-title">{{ news.title }}</h2>
                            </div>
                        </div>
                    </div>

                    <!-- Article Meta -->
                    <div class="article-meta mb-8">
                        <div class="meta-item">
                            <svg class="meta-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z">
                                </path>
                            </svg>
                            <span class="meta-text">{{ formattedDate }}</span>
                        </div>
                        <div v-if="news.creator" class="meta-item">
                            <svg class="meta-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                    d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path>
                            </svg>
                            <span class="meta-text">By {{ news.creator.name }}</span>
                        </div>
                        <div v-if="news.entity" class="meta-item entity-badge">
                            <img v-if="getEntityImage(news.entity)" class="entity-logo"
                                :src="getEntityImage(news.entity)" :alt="mapEntityName(news.entity)">
                            <span class="meta-text">{{ mapEntityName(news.entity) }}</span>
                        </div>
                    </div>

                    <!-- Thumbnail Image -->
                    <div v-if="news.thumbnail" class="article-thumbnail mb-8">
                        <div class="thumbnail-wrapper">
                            <img :src="getImage" :alt="news.title" class="thumbnail-image">
                            <div v-if="news.entity && getEntityImage(news.entity)" class="thumbnail-entity-badge">
                                <img class="entity-logo-small" :src="getEntityImage(news.entity)"
                                    :alt="mapEntityName(news.entity)">
                            </div>
                        </div>
                    </div>

                    <!-- Description -->
                    <div v-if="news.description" class="article-description mb-8">
                        <p class="description-text">{{ news.description }}</p>
                    </div>

                    <!-- Article Content -->
                    <div class="article-body">
                        <div class="prose-content" v-html="news.content"></div>
                    </div>
                </div>
            </div>
        </div>
    </section>
</template>

<script>
import gql from 'graphql-tag';
import IconBack from "../../components/icons/IconBack.vue";
import backgroundImage from '../../assets/images/news/img1.png';
import BreadCrumb from "../../components/shared/BreadCrumb.vue";
import FCHSLogo from '@/assets/images/dashboard/alumni/FCHS.png';
import ADPOLYLogo from '@/assets/images/dashboard/alumni/ADPOLY.png';
import ADVETILogo from '@/assets/images/dashboard/alumni/ADVETI.png';
import ATSLogo from '@/assets/images/dashboard/alumni/ATS.png';

const GET_NEWS_BY_SLUG_QUERY = gql`
    query GetNewsBySlug($slug: String!) {
        newsBySlug(slug: $slug) {
            error
            error_code
            message
            data {
                id
                title
                slug
                description
                content
                thumbnail
                news_date
                entity
                created_at
                creator {
                    id
                    name
                }
            }
        }
    }
`;

export default {
    name: "my-news",
    props: ["slug"],
    components: {
        IconBack,
        BreadCrumb,
    },
    data() {
        return {
            news: null,
            loading: true,
            error: null,
            breadCrumbItems: [
                { label: 'Home', path: '/' },
                { label: 'Latest News', path: '/news' },
                { label: 'News' },
            ],
        };
    },
    computed: {
        backgroundStyle() {
            return {
                'background-image': `url(${backgroundImage})`
            }
        },
        getImage() {
            if (!this.news || !this.news.thumbnail) return '';
            return this.resolveThumbnailUrl(this.news.thumbnail);
        },
        formattedDate() {
            if (!this.news || !this.news.news_date) return '';
            const date = new Date(this.news.news_date);
            const month = date.toLocaleString("default", { month: "short" });
            const day = date.getDate();
            const year = date.getFullYear();
            return `${month} ${day}, ${year}`;
        },
    },
    created() {
        this.loadNews();
    },
    methods: {
        async loadNews() {
            this.loading = true;
            this.error = null;

            try {
                const slug = this.slug;
                const { data } = await this.$apollo.query({
                    query: GET_NEWS_BY_SLUG_QUERY,
                    variables: { slug },
                    fetchPolicy: 'network-only',
                });

                if (!data || !data.newsBySlug) {
                    this.error = 'Failed to load news details.';
                    this.loading = false;
                    return;
                }

                const result = data.newsBySlug;

                if (result.error) {
                    this.error = result.message || 'News not found or not published.';
                    this.loading = false;
                    return;
                }

                if (!result.data) {
                    this.error = 'News not found.';
                    this.loading = false;
                    return;
                }

                this.news = result.data;
                this.breadCrumbItems[2].label = this.news.title;
            } catch (error) {
                console.error('Error loading news:', error);
                this.error = 'Failed to load news. Please try again.';
            } finally {
                this.loading = false;
            }
        },
        resolveThumbnailUrl(path) {
            if (!path) {
                return '';
            }
            if (typeof path === 'string' && (path.indexOf('http://') === 0 || path.indexOf('https://') === 0)) {
                return path;
            }
            const baseUrl = process.env.VUE_APP_PUBLIC_URL || '';
            return `${baseUrl}${path}`;
        },
        getEntityImage(entity) {
            if (!entity) return '';

            const entityStr = typeof entity === 'string' ? entity : '';
            const logos = {
                FCHS: FCHSLogo,
                ADPOLY: ADPOLYLogo,
                ADVETI: ADVETILogo,
                ATS: ATSLogo,
                ATHS: ATSLogo,
            };
            return logos[entityStr] || '';
        },
        mapEntityName(entity) {
            if (!entity) return '';
            return entity === 'ATHS' ? 'ATS' : entity;
        },
    },
}
</script>

<style lang="scss" scoped>
.news-hero {
    height: 400px;
    background-attachment: fixed;
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    position: relative;

    @media (min-width: 640px) {
        height: 450px;
    }

    @media (min-width: 1024px) {
        height: 500px;
    }
}

.hero-overlay {
    position: absolute;
    inset: 0;
    background: linear-gradient(135deg, rgba(57, 72, 103, 0.85) 0%, rgba(73, 157, 212, 0.75) 100%);
    z-index: 1;
}

.hero-content {
    position: relative;
    z-index: 2;
    height: 100%;
    color: white;
}

.hero-badge {
    display: inline-block;
    margin-bottom: 1rem;
}

.hero-badge span {
    display: inline-flex;
    align-items: center;
    padding: 0.5rem 1rem;
    border-radius: 9999px;
    font-size: 0.75rem;
    font-weight: 600;
    background: rgba(255, 255, 255, 0.2);
    backdrop-filter: blur(10px);
    color: white;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    border: 1px solid rgba(255, 255, 255, 0.3);
}

.hero-title {
    font-size: 2rem;
    font-weight: 700;
    margin-bottom: 0;
    line-height: 1.2;
    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);

    @media (min-width: 640px) {
        font-size: 2.5rem;
    }

    @media (min-width: 1024px) {
        font-size: 3rem;
    }
}

.section-header {
    padding-bottom: 1rem;
    border-bottom: 2px solid #e2e8f0;
}

.back-button-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 2.5rem;
    height: 2.5rem;
    background: linear-gradient(135deg, #394867 0%, #499DD4 100%);
    color: white;
    border-radius: 0.5rem;
    border: none;
    cursor: pointer;
    transition: all 0.3s ease;
    flex-shrink: 0;

    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 8px rgba(57, 72, 103, 0.3);
    }

    .icon {
        width: 1.25rem;
        height: 1.25rem;
    }
}

.section-title {
    font-size: 1.5rem;
    font-weight: 700;
    color: #1e293b;
    margin-bottom: 0;
    line-height: 1.3;

    @media (min-width: 640px) {
        font-size: 1.75rem;
    }

    @media (min-width: 1024px) {
        font-size: 2rem;
    }
}

.article-meta {
    display: flex;
    flex-wrap: wrap;
    gap: 1.5rem;
    padding: 1.5rem;
    background: white;
    border-radius: 0.75rem;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
    border: 1px solid #e2e8f0;
}

.meta-item {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    color: #64748b;
    font-size: 0.875rem;
    font-weight: 500;

    @media (min-width: 640px) {
        font-size: 0.9375rem;
    }
}

.meta-icon {
    width: 1.125rem;
    height: 1.125rem;
    color: #499DD4;
    flex-shrink: 0;
}

.meta-text {
    color: #475569;
}

.entity-badge {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 0.75rem;
    background: #f8fafc;
    border-radius: 0.5rem;
    border: 1px solid #e2e8f0;
}

.entity-logo {
    height: 1.25rem;
    width: auto;
    object-fit: contain;
}

.article-thumbnail {
    border-radius: 1rem;
    overflow: hidden;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
}

.thumbnail-wrapper {
    position: relative;
    width: 100%;
    padding-top: 56.25%; // 16:9 aspect ratio
    background: #f0f4f8;
    overflow: hidden;
}

.thumbnail-image {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.thumbnail-entity-badge {
    position: absolute;
    top: 1rem;
    right: 1rem;
    background: rgba(255, 255, 255, 0.95);
    backdrop-filter: blur(10px);
    padding: 0.5rem 0.75rem;
    border-radius: 0.5rem;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    border: 1px solid rgba(255, 255, 255, 0.8);
}

.entity-logo-small {
    height: 1.5rem;
    width: auto;
    object-fit: contain;
}

.article-description {
    padding: 1.5rem;
    background: white;
    border-radius: 0.75rem;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
    border-left: 4px solid #499DD4;
}

.description-text {
    font-size: 1.125rem;
    line-height: 1.7;
    color: #475569;
    font-weight: 500;

    @media (min-width: 640px) {
        font-size: 1.25rem;
    }
}

.article-body {
    background: white;
    padding: 2rem;
    border-radius: 0.75rem;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
    border: 1px solid #e2e8f0;
}

.prose-content {
    max-width: none;
    color: #1e293b;
    line-height: 1.8;
    font-size: 1rem;

    @media (min-width: 640px) {
        font-size: 1.125rem;
    }

    h1,
    h2,
    h3,
    h4,
    h5,
    h6 {
        font-weight: 700;
        margin-top: 2em;
        margin-bottom: 1em;
        color: #1e293b;
        line-height: 1.3;
    }

    h1 {
        font-size: 2.25em;
    }

    h2 {
        font-size: 1.875em;
    }

    h3 {
        font-size: 1.5em;
    }

    p {
        margin-bottom: 1.5em;
        line-height: 1.8;
    }

    img {
        max-width: 100%;
        height: auto;
        display: block;
        margin: 2em auto;
        border-radius: 0.75rem;
        box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
    }

    a {
        color: #499DD4;
        text-decoration: underline;
        transition: color 0.2s ease;

        &:hover {
            color: #394867;
        }
    }

    ul,
    ol {
        margin-top: 1.5em;
        margin-bottom: 1.5em;
        padding-left: 2em;
    }

    ul {
        list-style-type: disc;
    }

    ol {
        list-style-type: decimal;
    }

    li {
        margin-bottom: 0.75em;
        line-height: 1.8;
    }

    blockquote {
        border-left: 4px solid #499DD4;
        padding-left: 1.5em;
        margin-left: 0;
        margin-right: 0;
        margin-top: 2em;
        margin-bottom: 2em;
        font-style: italic;
        color: #64748b;
        background: #f8fafc;
        padding: 1.5em;
        border-radius: 0.5rem;
    }

    table {
        width: 100%;
        border-collapse: collapse;
        margin-top: 2em;
        margin-bottom: 2em;
        overflow-x: auto;
        display: block;

        @media (min-width: 768px) {
            display: table;
        }
    }

    th,
    td {
        border: 1px solid #e2e8f0;
        padding: 0.75em 1em;
        text-align: left;
    }

    th {
        background-color: #f8fafc;
        font-weight: 700;
        color: #1e293b;
    }

    code {
        background: #f1f5f9;
        padding: 0.2em 0.4em;
        border-radius: 0.25rem;
        font-size: 0.9em;
        color: #e11d48;
    }

    pre {
        background: #1e293b;
        color: #f1f5f9;
        padding: 1.5em;
        border-radius: 0.5rem;
        overflow-x: auto;
        margin: 2em 0;

        code {
            background: transparent;
            color: inherit;
            padding: 0;
        }
    }
}

.loading-container {
    background: white;
    border-radius: 0.75rem;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
    border: 1px solid #e2e8f0;
    overflow: hidden;
}

.loading-header {
    padding: 1rem 1.5rem;
    background: linear-gradient(90deg, rgba(34, 42, 57, 1) 0%, rgba(10, 66, 117, 1) 100%);
}

.loading-bar {
    height: 0.5rem;
    background: rgba(255, 255, 255, 0.2);
    border-radius: 9999px;
    animation: pulse 1.5s infinite ease-in-out;
}

.loading-content {
    padding: 2rem;
}

.loading-image {
    width: 100%;
    height: 400px;
    background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
    background-size: 200% 100%;
    animation: shimmer 1.5s infinite;
    border-radius: 0.75rem;
    margin-bottom: 2rem;
}

.loading-title {
    height: 2rem;
    background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
    background-size: 200% 100%;
    animation: shimmer 1.5s infinite;
    border-radius: 0.25rem;
    margin-bottom: 1rem;
    width: 70%;
}

.loading-meta {
    height: 1rem;
    background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
    background-size: 200% 100%;
    animation: shimmer 1.5s infinite;
    border-radius: 0.25rem;
    margin-bottom: 1.5rem;
    width: 40%;
}

.loading-description {
    height: 1.5rem;
    background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
    background-size: 200% 100%;
    animation: shimmer 1.5s infinite;
    border-radius: 0.25rem;
    margin-bottom: 2rem;
    width: 100%;
}

.loading-text-lines {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
}

.loading-line {
    height: 1rem;
    background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
    background-size: 200% 100%;
    animation: shimmer 1.5s infinite;
    border-radius: 0.25rem;

    &:nth-child(1) {
        width: 100%;
    }

    &:nth-child(2) {
        width: 95%;
    }

    &:nth-child(3) {
        width: 100%;
    }

    &:nth-child(4) {
        width: 90%;
    }

    &:nth-child(5) {
        width: 100%;
    }

    &:nth-child(6) {
        width: 85%;
    }

    &:nth-child(7) {
        width: 100%;
    }

    &:nth-child(8) {
        width: 92%;
    }
}

@keyframes shimmer {
    0% {
        background-position: -200% 0;
    }

    100% {
        background-position: 200% 0;
    }
}

.error-container {
    background: white;
    border-radius: 0.75rem;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
    border: 1px solid #e2e8f0;
    overflow: hidden;
}

.error-header {
    padding: 1rem 1.5rem;
    background: linear-gradient(90deg, rgba(34, 42, 57, 1) 0%, rgba(10, 66, 117, 1) 100%);
    display: flex;
    align-items: center;
    gap: 1rem;
}

.error-title {
    font-size: 1.5rem;
    font-weight: 700;
    color: white;

    @media (min-width: 640px) {
        font-size: 1.75rem;
    }
}

.error-content {
    padding: 3rem 1.5rem;
    text-align: center;
}

.error-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 4rem;
    height: 4rem;
    background: linear-gradient(135deg, #fee2e2 0%, #fecaca 100%);
    border-radius: 50%;
    margin-bottom: 1.5rem;
    color: #dc2626;

    svg {
        width: 2rem;
        height: 2rem;
    }
}

.error-message {
    font-size: 1.125rem;
    color: #64748b;
    margin-bottom: 2rem;
    max-width: 32rem;
    margin-left: auto;
    margin-right: auto;

    @media (min-width: 640px) {
        font-size: 1.25rem;
    }
}

.error-back-btn {
    display: inline-flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.875rem 2rem;
    background: linear-gradient(135deg, #394867 0%, #499DD4 100%);
    color: white;
    border-radius: 0.75rem;
    font-weight: 600;
    font-size: 1rem;
    border: none;
    cursor: pointer;
    transition: all 0.3s ease;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);

    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 8px 12px -2px rgba(0, 0, 0, 0.15);
        background: linear-gradient(135deg, #499DD4 0%, #394867 100%);
    }
}

.error-back-icon {
    width: 1.25rem;
    height: 1.25rem;
    transition: transform 0.3s ease;
}

.error-back-btn:hover .error-back-icon {
    transform: translateX(-4px);
}
</style>